import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import ReactCrop, { Crop } from "react-image-crop-v11"
import classNames from "classnames"
import Button from "customerAttachments/components/Button"
import PageLoading from "customerAttachments/components/Loading"
import { useModal } from "customerAttachments/contexts/ModalProvider"
import RotateIcon from "customerAttachments/icons/Icon-rotate.svg"
import UploadIcon from "customerAttachments/icons/Icon-upload.svg"
import { toBase64 } from "customerAttachments/utils/fileUtil"
import { addWatermark, resizeImage } from "services/utils/imageHelper"

import "react-image-crop/dist/ReactCrop.css"
import styles from "./styles.module.scss"

type ImageCropUploadProps = {
  onChange: (file: { FileName: string; Base64: string }) => void
  id: string
  text: string
}

const acceptedFileTypes = ["image/jpeg", "image/png", "image/heic", "image/heif"]
const MAX_IMAGE_SIZE = 10 // 10MB

const ImageCropUpload = ({ onChange, id, text }: ImageCropUploadProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined)
  const [rotation, setRotation] = useState<number>(0)
  const [imageParsing, setImageParsing] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const uploaderRef = useRef<HTMLInputElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const [crop, setCrop] = useState<Crop>({ x: 0, y: 0, unit: "%", width: 100, height: 100 })
  const { t } = useTranslation(["sales", "label"])
  const { openModal } = useModal()

  useEffect(() => {
    if (previewUrl) {
      return () => URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const resetCropStates = () => {
    setConfirm(false)
    setRotation(0)
    setCrop({ x: 0, y: 0, unit: "%", width: 100, height: 100 })
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0]

      if (!acceptedFileTypes.includes(selectedFile.type)) {
        openModal({
          title: t("customer_attachments.select_image"),
          content: t("upload.text_please_check_file"),
          showCancel: false,
        })
        return
      }

      if (selectedFile.size > MAX_IMAGE_SIZE * 1024 * 1024) {
        openModal({
          title: t("upload.title_file_too_large"),
          content: t("upload.text_file_too_large", { limit: MAX_IMAGE_SIZE }),
          showCancel: false,
        })
        return
      }

      resetCropStates()
      setFile(selectedFile)

      try {
        setImageParsing(true)
        const base64String = await toBase64(selectedFile, false)
        const resizedImage = await resizeImage(base64String)
        setPreviewUrl(resizedImage)
      } catch (error) {
        console.error("Failed to resize image:", error)
      } finally {
        setImageParsing(false)
      }
    }

    if (uploaderRef.current) {
      uploaderRef.current.value = ""
    }
  }

  const handleRotate = () => {
    setRotation((prevRotation) => (prevRotation + 90) % 360)
  }

  const getCroppedImg = async (image: HTMLImageElement, crop: Crop, rotation = 0): Promise<string> => {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    if (!ctx || !crop.width || !crop.height) {
      throw new Error("Canvas context or crop dimensions are invalid")
    }

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    canvas.width = crop.width * scaleX
    canvas.height = crop.height * scaleY

    const cropX = crop.x * scaleX
    const cropY = crop.y * scaleY

    const rotateRads = (rotation * Math.PI) / 180
    const centerX = image.naturalWidth / 2
    const centerY = image.naturalHeight / 2

    ctx.save()

    ctx.translate(-cropX, -cropY)
    ctx.translate(centerX, centerY)
    ctx.rotate(rotateRads)
    ctx.translate(-centerX, -centerY)
    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight)

    ctx.restore()

    return new Promise<string>((resolve, reject) => {
      canvas.toBlob(async (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"))
          return
        }

        try {
          const file = new File([blob], "croppedImage.jpg", { type: blob.type })
          const base64String = await toBase64(file, false)
          const resizedImage = await resizeImage(base64String)
          resolve(resizedImage)
        } catch (err) {
          reject(err)
        }
      }, "image/jpeg")
    })
  }

  const makeClientCrop = async (crop: Crop) => {
    if (imageRef.current && crop.width && crop.height) {
      const croppedImage = await getCroppedImg(imageRef.current, crop, rotation)
      return croppedImage
    }
    return null
  }

  const handleComplete = async () => {
    if (!file) return
    setIsLoading(true)
    let croppedImage = (await makeClientCrop(crop)) || ""
    if (croppedImage) {
      if (id === "IdCardFront") {
        croppedImage = await addWatermark({
          inputImage: croppedImage,
        })
      }
      if (id === "IdCardBack") {
        croppedImage = await addWatermark({
          inputImage: croppedImage,
          position: "left",
        })
      }
      setPreviewImage(croppedImage)
      onChange({
        FileName: file.name,
        Base64: croppedImage.split(",")[1],
      })
    }

    setConfirm(true)
    setRotation(0)
    setIsLoading(false)
  }

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget
    setCrop({
      unit: "px",
      x: 0,
      y: 0,
      width,
      height,
    })
  }

  const handleCancel = () => {
    setConfirm(false)
    setPreviewUrl(undefined)
    setRotation(0)
  }

  return (
    <>
      <div
        className={classNames(styles.upload, {
          [styles.hidden]: previewImage,
        })}>
        <PageLoading loading={imageParsing}>
          <input
            accept={acceptedFileTypes.join(",")}
            ref={uploaderRef}
            name={id}
            id={id}
            type="file"
            onChange={handleChange}
            className={styles.inputFile}
            data-testid={`${id}-input`}
          />
          <label className={styles.inputLabel} htmlFor={id}>
            <img alt="upload_icon" className={styles.uploadIcon} src={UploadIcon} />
            {text}
          </label>
        </PageLoading>
      </div>
      {previewUrl && !confirm && (
        <div className={styles.modal}>
          <div className={styles.back} onClick={handleCancel}>
            {`< ${t("order.subscribe_modify_btn_back")}`}
          </div>
          <img alt="rotate_icon" src={RotateIcon} className={styles.rotateIcon} onClick={handleRotate} />
          <ReactCrop
            crop={crop}
            onChange={(c) => {
              setCrop(c)
            }}
            onComplete={makeClientCrop}
            style={{ maxHeight: `calc(100vh - 250px)`, maxWidth: `calc(100vw - 80px)`, top: "70px" }}>
            <img
              alt="preview_image"
              src={previewUrl}
              ref={imageRef}
              onLoad={onImageLoad}
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          </ReactCrop>
          <Button outline onClick={handleComplete} loading={isLoading}>
            {t("label:finish")}
          </Button>
        </div>
      )}
      {previewImage && (
        <div
          className={styles.imagePreview}
          style={{ background: `center / contain no-repeat url(${previewImage})` }}
          onClick={() => uploaderRef.current?.click()}
        />
      )}
    </>
  )
}

export default ImageCropUpload
